import { ColorEnum } from '@/constants/color'
import { isIOS } from '@/constants/environment'
import { PriceItem } from '@/features/common/components/Overview/types'
import Button from '@/features/openTabs/components/AccountActionButton'
import DetailTabs, {
    DetailTab,
} from '@/features/openTabs/components/AccountDetail/parts/DetailTabs'
import History, {
    HistoryEventView,
} from '@/features/openTabs/components/AccountDetail/parts/History'
import OrderList, { OrderView } from '@/features/openTabs/components/AccountDetail/parts/OrderList'
import AccountFooter from '@/features/openTabs/components/AccountFooter'
import AccountHeader from '@/features/openTabs/components/AccountHeader'
import CatalogFooter from '@/features/openTabs/components/CatalogFooter'
import EmptyState from '@/features/openTabs/components/EmptyState'
import { getAccountItemCount } from '@/features/openTabs/utils/accountCalculations'
import Typography from '@/features/ui/typography'
import { useScreenLayout } from '@/hooks/screen'
import { FC, ReactNode, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface AccountDetailProps {
    accountNumber: number
    accountName: string
    closed: boolean
    events: HistoryEventView[]
    orders: OrderView[]
    totalPrice: string
    priceItems: PriceItem[]
    activeTab: DetailTab
    catalogMode: boolean
    catalogHasSelection: boolean
    catalogSubmitting?: boolean
    catalogBusy?: boolean
    catalogRefreshFailed?: boolean
    onRetryCatalogRefresh?: () => void
    catalogTotalPrice: string
    catalogPriceItems: PriceItem[]
    draftCart?: ReactNode
    submittedItemsBusy?: boolean
    submittedItemsRefreshFailed?: boolean
    onRetrySubmittedItemsRefresh?: () => void
    onChangeItemQuantity?: (itemId: string, change: -1 | 1) => void
    onRemoveItem?: (itemId: string) => void
    canPay: boolean
    onTabChange: (tab: DetailTab) => void
    onRename?: () => void
    onClose: () => void
    onReprint?: (orderId: string) => void
    reprintDisabled?: boolean
    onAddOrder: () => void
    onSplit?: () => void
    onPayAll: () => void
    onCancelCatalog: () => void
    onConfirmCatalog: () => void
}

const AccountDetail: FC<AccountDetailProps> = ({
    accountNumber,
    accountName,
    closed,
    orders,
    events,
    totalPrice,
    priceItems,
    activeTab,
    catalogMode,
    catalogHasSelection,
    catalogSubmitting = false,
    catalogBusy = false,
    catalogRefreshFailed = false,
    onRetryCatalogRefresh,
    catalogTotalPrice,
    catalogPriceItems,
    draftCart,
    submittedItemsBusy,
    submittedItemsRefreshFailed,
    onRetrySubmittedItemsRefresh,
    onChangeItemQuantity,
    onRemoveItem,
    canPay,
    onTabChange,
    onRename,
    onClose,
    onReprint,
    reprintDisabled,
    onAddOrder,
    onSplit,
    onPayAll,
    onCancelCatalog,
    onConfirmCatalog,
}) => {
    const { t } = useTranslation('app')
    const { styles } = useStyles(stylesheet)
    const layout = useScreenLayout()
    const itemCount = getAccountItemCount(orders)

    const showsHistory = closed || (!catalogMode && activeTab === 'history')

    return (
        <View style={[styles.container, showsHistory && styles.historyContainer]}>
            <AccountHeader
                accountNumber={accountNumber}
                accountName={accountName}
                closed={closed}
                onRename={onRename}
                onClose={onClose}
            />
            <View style={styles.content}>
                {catalogMode && catalogRefreshFailed && (
                    <View style={styles.refreshError}>
                        <Typography.Description>
                            {t('openTabs.feedback.refreshFailed')}
                        </Typography.Description>
                        <Button color="secondary" type="outline" onPress={onRetryCatalogRefresh}>
                            {t('openTabs.actions.retry')}
                        </Button>
                    </View>
                )}
                {submittedItemsRefreshFailed && (
                    <View style={styles.refreshError}>
                        <Typography.Description>
                            {t('openTabs.feedback.refreshFailed')}
                        </Typography.Description>
                        <Button
                            color="secondary"
                            type="outline"
                            onPress={onRetrySubmittedItemsRefresh}
                        >
                            {t('openTabs.actions.retry')}
                        </Button>
                    </View>
                )}
                {!closed && !catalogMode && (
                    <View style={styles.tabs}>
                        <DetailTabs
                            activeTab={activeTab}
                            itemCount={itemCount}
                            historyCount={events.length}
                            onChange={onTabChange}
                        />
                    </View>
                )}
                {catalogMode && catalogHasSelection && (
                    <Typography.Heading variant="h5" style={styles.closedTitle}>
                        {t('openTabs.detail.items')}
                    </Typography.Heading>
                )}
                {closed && (
                    <Typography.Heading variant="h5" style={styles.closedTitle}>
                        {t('openTabs.detail.orderHistory')}
                    </Typography.Heading>
                )}
                <ScrollView
                    style={styles.scroll}
                    contentContainerStyle={[
                        styles.scrollContent,
                        !showsHistory && styles.itemsContent,
                    ]}
                    showsVerticalScrollIndicator={false}
                >
                    {catalogMode && !closed ? (
                        catalogHasSelection ? (
                            draftCart
                        ) : (
                            <EmptyState
                                title={t('openTabs.empty.accountTitle')}
                                description={t('openTabs.empty.catalogDescription')}
                            />
                        )
                    ) : showsHistory ? (
                        events.length ? (
                            <History events={events} />
                        ) : (
                            <EmptyState
                                title={t('openTabs.empty.historyTitle')}
                                description={t('openTabs.empty.historyDescription')}
                            />
                        )
                    ) : orders.length ? (
                        <OrderList
                            orders={orders}
                            onReprint={onReprint}
                            reprintDisabled={reprintDisabled}
                            disabled={submittedItemsBusy}
                            onChangeQuantity={onChangeItemQuantity}
                            onRemove={onRemoveItem}
                        />
                    ) : (
                        <EmptyState
                            title={t('openTabs.empty.accountTitle')}
                            description={t('openTabs.empty.accountDescription')}
                        />
                    )}
                </ScrollView>
            </View>
            {!closed &&
                (catalogMode ? (
                    <CatalogFooter
                        totalPrice={catalogTotalPrice}
                        priceItems={catalogPriceItems}
                        canConfirm={catalogHasSelection && !catalogBusy && !catalogSubmitting}
                        submitting={catalogSubmitting}
                        onCancel={onCancelCatalog}
                        onConfirm={onConfirmCatalog}
                    />
                ) : (
                    <AccountFooter
                        totalPrice={totalPrice}
                        priceItems={priceItems}
                        canSplit={itemCount > 0 && !!onSplit && canPay && !submittedItemsBusy}
                        canPay={itemCount > 0 && canPay && !submittedItemsBusy}
                        onAddOrder={onAddOrder}
                        onPayAll={onPayAll}
                        onSplit={onSplit}
                    />
                ))}
            {closed && (
                <View
                    style={[
                        styles.closedFooter,
                        layout === 'compact' && styles.closedFooterCompact,
                    ]}
                >
                    <View style={styles.closedTotal}>
                        <Typography.Heading variant="h4" style={styles.closedTotalLabel}>
                            {t('openTabs.summary.total')}
                        </Typography.Heading>
                        <Typography.Heading variant="h4">{totalPrice}</Typography.Heading>
                    </View>
                </View>
            )}
        </View>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    refreshError: { paddingHorizontal: 16, gap: 8, marginBottom: 8 },
    container: {
        flex: 1,
        minWidth: 0,
        backgroundColor: ColorEnum.WHITE,
        boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.25)',
    },
    historyContainer: {
        boxShadow: '-4px 0px 4px rgba(0, 0, 0, 0.25)',
    },
    content: {
        flex: 1,
        minHeight: 0,
        paddingTop: 20,
    },
    tabs: {
        height: 36,
        justifyContent: 'center',
        paddingHorizontal: 16,
        marginBottom: 8,
    },
    closedTitle: {
        height: 36,
        justifyContent: 'center',
        paddingHorizontal: 16,
    },
    scroll: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
        paddingBottom: 16,
    },
    itemsContent: {
        paddingHorizontal: 16,
    },
    closedFooter: {
        height: 122,
        paddingTop: 8,
        paddingHorizontal: 16,
        paddingBottom: 16,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        backgroundColor: ColorEnum.VIOLET_100,
    },
    closedFooterCompact: {
        height: 'auto',
        paddingBottom: isIOS ? Math.max(16, insets.bottom) : 16 + insets.bottom,
    },
    closedTotal: {
        minHeight: 42,
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: ColorEnum.GRAY_300,
    },
    closedTotalLabel: {
        flex: 1,
    },
}))

export default memo(AccountDetail)
